import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { isStrictNumber } from "utils/regex/isStrictNumber";
import { getDecimalPlaces } from "utils/math/getDecimalPlaces";
import { isPotentialNumberText } from "utils/regex/isPotentialNumberText";
import {
  NumberInputProps,
  ClampNumber,
  CommitNumberIfValid,
  HandleTextChange,
  ParseValidNumberFromText,
  StepByDelta,
  HandleInputKeyDown,
} from "./types";

const isInProgressNumberText = (text: string) =>
  text === "" || text === "-" || text === "." || text === "-.";

export const NumberInput = ({
  value,
  onChange,
  onBlur,
  step = 0.01,
  min = -Infinity,
  max = Infinity,
  className,
  inputClassName,
  decButtonClassName,
  incButtonClassName,
  inputMode = "decimal",
  showButtons = true,
}: NumberInputProps) => {
  const [draftText, setDraftText] = useState(() => String(value));
  const [isEditing, setIsEditing] = useState(false);

  const clampNumber: ClampNumber = useMemo(
    () => (inputNumber) => Math.min(max, Math.max(min, inputNumber)),
    [min, max],
  );

  const revertDraftTextToCommittedValue = useCallback(() => {
    setDraftText(String(value));
  }, [value]);

  const parseValidNumberFromText: ParseValidNumberFromText = useCallback(
    (text) => {
      const trimmedText = text.trim();
      if (isInProgressNumberText(trimmedText) || !isStrictNumber(trimmedText)) {
        return null;
      }
      const parsedNumber = Number(trimmedText);
      return Number.isFinite(parsedNumber) ? clampNumber(parsedNumber) : null;
    },
    [clampNumber],
  );

  const commitNumberIfValid: CommitNumberIfValid = useCallback(
    (text) => {
      const nextNumberOrNull = parseValidNumberFromText(text);
      if (nextNumberOrNull !== null) {
        onChange(nextNumberOrNull);
      }
    },
    [onChange, parseValidNumberFromText],
  );

  const handleTextChange: HandleTextChange = useCallback(
    (text) => {
      if (!isPotentialNumberText(text)) return;

      setDraftText(text);
      commitNumberIfValid(text);
    },
    [commitNumberIfValid],
  );

  const handleInputBlur = useCallback(() => {
    setIsEditing(false);
    const parsedNumberOrNull = parseValidNumberFromText(draftText);
    if (parsedNumberOrNull === null) {
      revertDraftTextToCommittedValue();
      return;
    }
    setDraftText(String(parsedNumberOrNull));
    onBlur?.();
  }, [
    draftText,
    parseValidNumberFromText,
    revertDraftTextToCommittedValue,
    onBlur,
  ]);

  const stepByDelta: StepByDelta = useCallback(
    (delta) => {
      const rawNext = clampNumber(value + delta);
      const decimals = getDecimalPlaces(step);
      const formattedNext = Number(rawNext.toFixed(decimals)); // 37.44 not 37.440000000000005
      onChange(formattedNext);
      setDraftText(formattedNext.toString());
    },
    [clampNumber, onChange, step, value],
  );

  const handleInputKeyDown: HandleInputKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.currentTarget.blur();
      }
      if (event.key === "Escape") {
        revertDraftTextToCommittedValue();
        event.currentTarget.blur();
      }
    },
    [revertDraftTextToCommittedValue],
  );

  useEffect(() => {
    if (!isEditing) {
      setDraftText(String(value));
    }
  }, [value, isEditing]);

  return (
    <div
      className={clsx(
        "inline-flex items-stretch w-full rounded-md border border-gray-200 bg-white text-sm overflow-hidden",
        "focus-within:border-blue-400",
        className,
      )}
    >
      {showButtons && (
        <button
          type="button"
          onClick={() => stepByDelta(-step)}
          className={clsx(
            "px-2 flex items-center justify-center",
            "text-gray-500 hover:text-blue-600",
            "border-r border-gray-200",
            "select-none",
            "transition-colors hover:bg-blue-50 active:bg-blue-100",
            decButtonClassName,
          )}
        >
          −
        </button>
      )}
      <input
        type="text"
        inputMode={inputMode}
        value={draftText}
        onFocus={() => setIsEditing(true)}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        onChange={(event) => handleTextChange(event.target.value)}
        className={clsx(
          "min-w-0 flex-1 bg-transparent px-2 py-1 text-gray-900",
          "border-none outline-none focus:ring-0 focus:shadow-none",
          showButtons ? "text-center" : "text-left",
          inputClassName,
        )}
      />
      {showButtons && (
        <button
          type="button"
          onClick={() => stepByDelta(step)}
          className={clsx(
            "px-2 flex items-center justify-center",
            "text-gray-500 hover:text-blue-600",
            "border-l border-gray-200",
            "select-none",
            "transition-colors hover:bg-blue-50 active:bg-blue-100",
            incButtonClassName,
          )}
        >
          +
        </button>
      )}
    </div>
  );
};
